import { Box, Typography, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { ECElementEvent } from 'echarts';
import { getDoughnutChartWidgetOptions } from '../chartWidgetUtility';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';

interface DoughnutChartProps {
  isLoading: boolean;
  title?: string;
  totalText?: any;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  hasTooltip?: boolean;
  seriesData: { name: string; value: any; percentage: string; formattedName: string }[];
  handleEvent?: (event: ECElementEvent, title?: string) => void;
  dataLabel?: boolean;
  legendLabel?: boolean;
  hoursFormat?: boolean;
  donutHeight?: string | null;
  chartSize?: string;
  children?: React.ReactNode;
  isRMCost?: boolean;
  isCost?: boolean;
}

const DoughnutChart = ({
  isLoading = false,
  title = '',
  hasTooltip = false,
  tooltipLabel = '',
  totalText,
  seriesData,
  handleEvent,
  dataLabel = false,
  legendLabel = false,
  hoursFormat = false,
  chartSize,
  isCost = false,
}: Readonly<DoughnutChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [gridLeft] = useState('5%');

  const defaultOptions = {
    graphic: [
      {
        type: 'circle',
        left: 'center',
        top: 'center',
        shape: {
          r: 100,
        },
        style: {
          fill: '#71758f66',
        },
      },
      {
        type: 'circle',
        left: 'center',
        top: 'center',
        shape: {
          r: 60,
        },
        style: {
          fill: colors.widget.background.card,
        },
      },
    ],
    title: {
      show: true,
      textStyle: {
        color: colors.widget.text.main,
        fontWeight: 'normal',
        fontSize: 16,
      },
      text: 'No data',
      left: 'center',
      top: 'center',
    },
    backgroundColor: colors.widget.background.card,
  };

  useEffect(() => {
    const onResize = debounce(() => setWindowSize(window.innerWidth), 200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const result = getDoughnutChartWidgetOptions(
    theme,
    totalText,
    seriesData,
    dataLabel,
    legendLabel,
    hoursFormat,
    isCost,
  );

  const adjustedOption: any = !seriesData?.length
    ? defaultOptions
    : {
        ...result,
        grid: {
          ...result.graphic,
          left: gridLeft,
        },
      };

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        p={2}
        bgcolor="primary.main"
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          color: `${colors.widget.text.main} !important`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="widgetTitle"
              sx={{ color: `${colors.widget.text.title} !important` }}
            >
              <HighlightedText text={title} />
            </Typography>
            {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>
        </Stack>
        <Stack
          flexGrow={1}
          justifyContent="center"
          key={windowSize}
          ref={chartContainerRef}
          minHeight={chartSize ? chartSize : '500px'}
        >
          {!isLoading ? (
            <ReactECharts
              style={{ width: '100%', height: '100%' }}
              option={adjustedOption}
              opts={{ renderer: 'canvas', width: 'auto' }}
              notMerge={true}
              lazyUpdate={true}
              onEvents={{
                click: (params: ECElementEvent) => {
                  if (handleEvent) {
                    handleEvent(params, title);
                  }
                },
              }}
            />
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default DoughnutChart;
